import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import ChartExportAsPNG from "../../../../Utils/ChartExportAsPng";

const MonoBarChart = ({ data, totalValue, type, containerClassName = "", exportRef, selectedCity, year }) => {
  const svgRef = useRef();
  const containerRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isVisible, setIsVisible] = useState(true);
  const color = "#2F4B7C";

  // Resize observer to update chart dimensions responsively
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        const height = Math.max(400, Math.min(580, width / 2));
        setDimensions({ width, height });
      }
    };

    handleResize();

    const resizeObserver = new ResizeObserver(handleResize);
    if (containerRef.current) resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (!data || data.length === 0) return;
    const margin = { top: 20, right: 40, bottom: 70, left: 60 };
    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;

    // Clear previous svg content
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    // Set svg attributes for responsiveness
    svg
      .attr("viewBox", `0 0 ${dimensions.width} ${dimensions.height}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .style("width", "100%")
      .style("height", "auto")
      .style("font-family", "sans-serif");

    // Scales
    const xScale = d3
      .scaleBand()
      .domain(data.map((d) => d.name))
      .range([0, width])
      .padding(0.2);

    const maxPercent = isVisible ? d3.max(data, (d) => (d.value / totalValue) * 100) : 100


    const yScale = d3
      .scaleLinear()
      .domain([0, maxPercent])
      .range([height, 0])
      .nice();

    // Axes font size based on width
    const axisFontSize = Math.max(10, Math.min(14, width / 40));

    // Grid
    svg
      .append("g")
      .attr("class", "grid y-grid")
      .attr("transform", `translate(${margin.left}, ${margin.top})`)
      .call(d3.axisLeft(yScale).tickSize(-width).tickFormat(""))
      .call((g) => g.selectAll(".domain").remove())
      .call((g) =>
        g.selectAll("line").attr("stroke", "#e0e0e0")
      );

    svg
      .append("g")
      .attr("class", "grid x-grid")
      .attr("transform", `translate(${margin.left}, ${height + margin.top})`)
      .call(d3.axisBottom(xScale).tickSize(-height).tickFormat(""))
      .call((g) => g.selectAll(".domain").remove())
      .call((g) =>
        g
          .selectAll("line")
          .attr("stroke", "#e0e0e0")
          .attr("shapeRendering", "crispEdges")
      );

    svg
      .append("g")
      .attr("class", "grid y-grid-right")
      .attr("transform", `translate(${width + margin.left}, ${margin.top})`)
      .call(d3.axisRight(yScale).tickSize(0).tickFormat(""))
      .call((g) =>
        g
          .selectAll(".domain")
          .attr("stroke", "#e0e0e0")
          .attr("shapeRendering", "crispEdges")
      )
      .call((g) => g.selectAll(".tick").remove());

    // X Axis
    svg
      .append("g")
      .attr("class", "x-axis")
      .attr("transform", `translate(${margin.left},${height + margin.top})`)
      .call(d3.axisBottom(xScale))
      .selectAll("text")
      .style("text-anchor", "middle")
      .attr("fill", "gray")
      .style("font-size", `${axisFontSize}px`)
      .style("text-anchor", dimensions.width < 1100 ? "end" : "middle")
      .attr("transform", dimensions.width < 1100 ? "rotate(-15)" : "rotate(0)")

    svg.select(".x-axis path").attr("stroke", "#e0e0e0");
    svg.select(".x-axis").selectAll(".tick line").attr("stroke", "#e0e0e0");

    // Y Axis
    svg
      .append("g")
      .attr("class", "y-axis")
      .attr("transform", `translate(${margin.left},${margin.top})`)
      .call(d3.axisLeft(yScale).ticks(Math.min(10, Math.ceil(height / 45))).tickFormat((d) => `${d}%`))
      .selectAll("text")
      .attr("fill", "gray")
      .style("font-size", `${axisFontSize}px`)


    svg.select(".y-axis path").attr("stroke", "#e0e0e0");
    svg.select(".y-axis").selectAll(".tick line").attr("stroke", "#e0e0e0");

    // Bars container
    const barsContainer = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Tooltip div
    const tooltip = d3
      .select("body")
      .selectAll(".tooltip")
      .data([null])
      .join("div")
      .attr("class", "tooltip")
      .style("position", "absolute")
      .style("background", "rgba(0, 0, 0, 0.7)")
      .style("color", "#fff")
      .style("padding", "6px 10px")
      .style("border-radius", "4px")
      .style("font-size", `${axisFontSize}px`)
      .style("pointer-events", "none")
      .style("opacity", 0)
      .style("z-index", 1000);

    // Bars
    barsContainer
      .selectAll("rect")
      .data(data)
      .join("rect")
      .attr("class", "bar")
      .attr("x", (d) => xScale(d.name))
      .attr("width", xScale.bandwidth())
      .attr("y", height)
      .attr("height", 0)
      .attr("fill", color)
      .attr("opacity", isVisible ? 1 : 0)
      .on("mouseover", (event, d) => {
        if (!isVisible) return;
        tooltip
          .style("opacity", 1)
          .html(
            `<strong>${d.name}</strong><br/>${type}: ${(
              (d.value / totalValue) *
              100
            ).toFixed(2)}%`
          )
          .style("left", `${event.pageX + 10}px`)
          .style("top", `${event.pageY - 28}px`);
        d3.select(event.currentTarget).attr("fill", d3.rgb(color).darker(0.5));
      })
      .on("mousemove", (event) => {
        tooltip.style("left", `${event.pageX + 10}px`).style("top", `${event.pageY - 28}px`);
      })
      .on("mouseout", (event) => {
        tooltip.style("opacity", 0);
        d3.select(event.currentTarget).attr("fill", color);
      })
      .transition()
      .duration(800)
      .attr("y", (d) => yScale((d.value / totalValue) * 100))
      .attr("height", (d) => Math.abs(height - yScale((d.value / totalValue) * 100)));

  }, [data, dimensions, isVisible, totalValue, type, color]);

  return (
    <div className="flex flex-col-reverse mt-5" style={{ width: "100%", height: "100%" }}>
      <div
        className={`flex justify-center w-full ${containerClassName} `}
        ref={containerRef}
        id="mono-bar-chart-svg"
      >
        <svg ref={svgRef} />
      </div>
      <div className="flex justify-center items-center gap-5    2xl:mt-3">
        <div
          onClick={() => setIsVisible((v) => !v)}
          className="space-x-3 inline-flex items-center cursor-pointer select-none"
        >
          <button
            className="w-10 h-3  lg:h-4 transition-colors duration-300"
            style={{ backgroundColor: isVisible ? color : "#d1d5db" }}
          ></button>
          <span
            className={`  text-sm md:text-base lg:text-lg transition-colors duration-300 ${isVisible ? "text-[#6c757d] " : "text-gray-400 line-through"
              }`}
          >
            {type}

          </span>
        </div>
      </div>

     <ChartExportAsPNG
        ref={exportRef}
        targetId="mono-bar-chart-svg"
        chartType="bar"
        targetWidth={600}
        fileNamePrefix={type + '- Bar Chart ' + selectedCity?.name}
        chartData={data}
        imageTitle={type + ' - Bar Chart'}
        imageSource={`${selectedCity?.name} ${(selectedCity?.type).charAt(0).toUpperCase() + (selectedCity.type).slice(1).toLowerCase()} - ${year}`}
        LegendFirstColumn={type}
        showColorBlock={false}
      />
    </div>
  );
};

export default MonoBarChart;
